import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import { checkLicensePermission } from '../../../../utility/communityLicense'
import { isOnlineOnly } from '../../../../utility/programHelper'
import { calculateAge, genderLabel } from '../../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../../utility/phoneHelper'
import { deserialize } from '../../../../utility/serialization'
import { requestGetWaitlistEntry } from '../../../../actions/waitlistActions'
import * as Constants from '../../../../misc/Constants'

import ProfileAvatar from '../../../common/avatar/ProfileAvatar'
import Loader from '../../../common/loader/Loader'
import WaitlistActionModal from '../enrollments-tab/waitlist/modals/WaitlistActionModal'
import AddStudentForm from '../enrollments-tab/waitlist/forms/AddStudentForm'
import RemoveStudentForm from '../enrollments-tab/waitlist/forms/RemoveStudentForm'

import { ReactComponent as CloseIcon } from '../../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as PlusIcon } from '../../../../assets/images/common/icons/circle-plus-icon.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/images/common/icons/delete-icon.svg'
import { ReactComponent as PersonIcon } from '../../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../../assets/images/common/icons/copy-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../../../assets/images/common/icons/golfer-half-icon.svg'

import classes from './WaitlistDetailDrawer.module.scss'

const TABS = { enrollments: 'enrollments', contact: 'contact' }
const ACTIONS = { add: 'ADD', remove: 'REMOVE' }

const WaitlistDetailDrawer = ({ waitlistEntry, program, onClose }) => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const [activeTab, setActiveTab] = useState(TABS.enrollments)
  const [action, setAction] = useState(null)
  const [details, setDetails] = useState(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(true)

  const student = waitlistEntry.user
  const coach = program?.coaches?.[0]
  const coachName = coach ? `${coach.firstName} ${coach.lastName}` : '-'
  const programDates = program?.startDate && program?.endDate
    ? `${moment(program.startDate).format('MM/DD/YYYY')}-${moment(program.endDate).format('MM/DD/YYYY')}`
    : '-'

  const canAddToProgram = !checkLicensePermission(community?.licenseType, [Constants.LICENSES.REVENUE_SHARE])
    && !isOnlineOnly(program?.paymentMethod)

  useEffect(() => {
    let active = true
    setIsLoadingDetails(true)
    requestGetWaitlistEntry(program?.id, waitlistEntry.id)
      .then(response => { if (active) { setDetails(deserialize(response.data)) } })
      .catch(() => toast.error('Failed to load contact details', { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => { if (active) { setIsLoadingDetails(false) } })
    return () => { active = false }
  }, [program?.id, waitlistEntry.id])

  const closeAction = () => setAction(null)
  const closeAfterAction = () => { setAction(null); onClose() }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.overlay} onClick={onClose} />
      <aside className={classes.drawer}>
        <div className={classes.header}>
          <button className={classes.closeButton} onClick={onClose} aria-label='Close'>
            <CloseIcon className={classes.closeIcon} />
          </button>
        </div>

        <div className={classes.tabs}>
          <button
            className={classNames(classes.tab, { [classes.tabActive]: activeTab === TABS.enrollments })}
            onClick={() => setActiveTab(TABS.enrollments)}
          >
            Enrollments
          </button>
          <button
            className={classNames(classes.tab, { [classes.tabActive]: activeTab === TABS.contact })}
            onClick={() => setActiveTab(TABS.contact)}
          >
            Contact Info
          </button>
        </div>

        <div className={classes.body}>
          <div className={classes.studentCard}>
            <ProfileAvatar student={student} />
            <div className='d-flex flex-column'>
              <span className={classes.studentName}>{student.fullName}</span>
              <span className={classes.studentEmail}>{student.email}</span>
            </div>
          </div>

          {activeTab === TABS.enrollments ? (
            <>
              <div className={classes.actions}>
                {canAddToProgram && (
                  <button className={classes.actionButton} onClick={() => setAction(ACTIONS.add)}>
                    <PlusIcon className={classes.actionIcon} />
                    Add to Program
                  </button>
                )}
                <button className={classes.actionButton} onClick={() => setAction(ACTIONS.remove)}>
                  <DeleteIcon className={classes.actionIcon} />
                  Remove
                </button>
              </div>

              <div className={classes.detailsCard}>
                <div className={classes.detailsHeader}>
                  <span className={classes.detailsTitle}>Details</span>
                  <span className={classes.waitlistBadge}>Waitlist</span>
                </div>

                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Program</span>
                  <span className={classes.detailValue}>{program?.name || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Facility</span>
                  <span className={classes.detailValue}>{community?.name || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Coach</span>
                  <span className={classes.detailValue}>{coachName}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Family Admin Email</span>
                  <span className={classes.detailValue}>{student.familyAdminsEmails?.[0] || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Program Dates</span>
                  <span className={classes.detailValue}>{programDates}</span>
                </div>

                <p className={classes.updatesTitle}>All Updates</p>
                <div className={classes.updateRow}>
                  <span className={classes.updateDot} />
                  <span className={classes.updateDate}>
                    {waitlistEntry.createdAt ? moment(waitlistEntry.createdAt).format('MM/DD/YYYY') : '-'}
                  </span>
                  <span className={classes.updateText}>
                    Student Joined Waitlist {waitlistEntry.createdAt ? moment(waitlistEntry.createdAt).format('h:mm A') : ''}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <ContactTab details={details} isLoading={isLoadingDetails} />
          )}
        </div>
      </aside>

      {action === ACTIONS.add && (
        <WaitlistActionModal
          isOpen
          onClose={closeAction}
          student={student}
          title='Add to Program'
          subtitle='Add this student to the program, they will receive an email.'
          form={<AddStudentForm course={program} waitlistEntry={waitlistEntry} closeModal={closeAfterAction} />}
        />
      )}

      {action === ACTIONS.remove && (
        <WaitlistActionModal
          isOpen
          onClose={closeAction}
          student={student}
          title='Remove from Waitlist'
          subtitle='Remove this student from the program, they will receive an email.'
          form={<RemoveStudentForm course={program} waitlistEntry={waitlistEntry} closeModal={closeAfterAction} />}
        />
      )}
    </>
  )
}

WaitlistDetailDrawer.propTypes = {
  waitlistEntry: PropTypes.object.isRequired,
  program: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

const ContactTab = ({ details, isLoading }) => {
  const navigate = useNavigate()

  if (isLoading || !details) {
    return <div className={classes.loaderWrap}><Loader /></div>
  }

  const student = details.student
  const familyAdmin = student.familyAdmins?.[0]
  const programs = details.communityEnrollments || []
  const hasGender = familyAdmin?.gender !== null && familyAdmin?.gender !== undefined

  const handleCopy = (e, text) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    toast.success('Copied email to clipboard!', { position: toast.POSITION.TOP_RIGHT })
  }

  return (
    <div className={classes.tabContent}>
      <section className={classes.quickActions}>
        <h6 className={classes.sectionTitle}>Quick Actions &amp; Administration</h6>
        <button className={classes.quickButton} onClick={() => navigate(`/profile/${student.id}`)}>
          <PersonIcon width={22} height={22} />
          View Profile
        </button>
        {student.familyId && (
          <button className={classes.quickButton} onClick={() => navigate(`/members/families/${student.familyId}`)}>
            <FamilyIcon width={22} height={22} />
            View Family Page
          </button>
        )}
      </section>

      {familyAdmin && (
        <section className={classes.contactSection}>
          <h6 className={classes.sectionTitle}>Contact Family Admin</h6>
          <div className={classes.adminProfile}>
            <ProfileAvatar student={familyAdmin} avatarClassName='small-round-image-wrapper' />
            <div className={classes.adminInfo}>
              <div className={classes.adminName}>
                <span className='font-weight-bold'>{familyAdmin.firstName} {familyAdmin.lastName}</span>
                <span className={classes.familyAdminLabel}>Family Admin</span>
              </div>
              <span className={classes.adminMeta}>
                {familyAdmin.birthdate && `Age ${calculateAge(familyAdmin.birthdate)}`}
                {familyAdmin.birthdate && hasGender && ' / '}
                {hasGender && genderLabel[familyAdmin.gender]?.charAt(0)}
              </span>
            </div>
          </div>

          {familyAdmin.phonePrefix && familyAdmin.phone && (
            <a className={classes.contactRow} href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}>
              <CallIcon className={classes.contactIcon} />
              <span>{familyAdmin.phonePrefix} {familyAdmin.phone}</span>
            </a>
          )}

          {familyAdmin.email && (
            <a className={classes.contactRow} href={`mailto:${familyAdmin.email}`}>
              <MailIcon className={classes.contactIcon} />
              <div className={classes.emailWrapper}>
                <button onClick={e => handleCopy(e, familyAdmin.email)} className={classes.copyButton} aria-label='Copy email address'>
                  <CopyIcon className={classes.copyIcon} />
                </button>
                {familyAdmin.email}
              </div>
            </a>
          )}
        </section>
      )}

      {programs.length > 0 && (
        <section className={classes.programsSection}>
          <h6 className={classes.sectionTitle}>Programs Currently Enrolled in</h6>
          {programs.map(enrollment => (
            <div key={enrollment.id} className={classes.programCard}>
              <span className={classes.programName}>{enrollment.courseName}</span>
              {enrollment.courseStartDate && (
                <span className={classes.programMetaRow}>
                  <CalendarIcon className={classes.programCalendarIcon} />
                  <span>
                    {moment(enrollment.courseStartDate).format('MMM D, YYYY')}
                    {enrollment.courseEndDate && ` - ${moment(enrollment.courseEndDate).format('MMM D, YYYY')}`}
                  </span>
                </span>
              )}
              {(enrollment.golfersCount > 0 || enrollment.courseMeetingTimes) && (
                <span className={classes.programMetaRow}>
                  {enrollment.golfersCount > 0 && (
                    <>
                      <GolferIcon className={classes.programGolferIcon} />
                      <span className={classes.programGolferCount}>{enrollment.golfersCount}</span>
                    </>
                  )}
                  {enrollment.courseMeetingTimes && <span>{enrollment.courseMeetingTimes}</span>}
                </span>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

ContactTab.propTypes = {
  details: PropTypes.object,
  isLoading: PropTypes.bool,
}

export default WaitlistDetailDrawer
